import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import RoleGuard from '../../../components/guards/RoleGuard';
import AccessDenied from '../../../components/access-denied/AccessDenied';
import CompanyAuditTable from './components/CompanyAuditTable';
import AuditLockedPreview from './components/AuditLockedPreview';
import useCompanyAudit from './hooks/useCompanyAudit';

/**
 * CompanyAudit - Auditoria de acessos e alterações de pacientes da clínica (LGPD L-14)
 *
 * Só o proprietário da clínica enxerga esta tela (RoleGuard); os demais papéis veem a
 * prévia bloqueada (AccessDenied), mesmo padrão de Statistics/Financial/Reports. O papel é
 * conferido antes de montar a lista, para quem não pode não disparar a chamada.
 * Consome useCompanyAudit() para a lista paginada/filtrada de audit_log.php. Os filtros
 * (e o paciente, quando a tela chega pelo atalho da ficha) vivem na URL para que o link
 * possa ser compartilhado.
 */
function CompanyAudit() {
  return (
    <RoleGuard
      access="audit"
      fallback={(
        <AccessDenied
          title="A auditoria é de quem é proprietário(a)"
          description="O histórico de quem abriu, alterou ou baixou cada cadastro aparece só para quem é proprietário(a) da conta."
          hint="Se precisar conferir um acesso, peça a quem é proprietário(a) da conta."
          preview={<AuditLockedPreview />}
        />
      )}
    >
      <CompanyAuditContent />
    </RoleGuard>
  );
}

function CompanyAuditContent() {
  const {
    entries,
    pagination,
    actions,
    users,
    patient,
    isLoading,
    error,

    patientId,
    clearPatient,
    action,
    setAction,
    userId,
    setUserId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    setPage,
  } = useCompanyAudit();

  return (
    <div className="page-content">
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Auditoria"
          Icon={<FactCheckOutlinedIcon />}
          Description="Quem abriu, alterou ou baixou dados de cada paciente, com data, usuário e IP."
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Registros: <strong>{pagination.total}</strong>
        </Typography>
        {patientId && (
          <Chip
            size="small"
            label={`Paciente: ${patient?.name || `#${patientId}`}`}
            onDelete={clearPatient}
            sx={{ bgcolor: '#fff', border: '1px solid #E0E0E0' }}
          />
        )}
      </Box>

      <CompanyAuditTable
        entries={entries}
        isLoading={isLoading}
        error={error}
        pagination={pagination}
        onPageChange={setPage}
        hidePatientColumn={Boolean(patientId)}
        filters={{
          actions,
          action,
          onActionChange: setAction,
          users,
          userId,
          onUserChange: setUserId,
          dateFrom,
          onDateFromChange: setDateFrom,
          dateTo,
          onDateToChange: setDateTo,
        }}
      />
    </div>
  );
}

export default CompanyAudit;
